<template>
    <div class="delivery-page">
        <div v-if="pullRefreshing" class="delivery-pull-refresh" role="status">
            <span class="spinner-border spinner-border-sm text-teal-600" aria-hidden="true"></span>
            <span class="visually-hidden">{{$t('delivery.loading_orders')}}</span>
        </div>

        <div class="container">
            <div class="row mb-3">
                <div class="col-12">
                    <div class="delivery-toolbar d-flex align-items-center gap-2 bg-white">
                        <form class="delivery-toolbar__search flex-fill" role="search" @submit="submit">
                            <label for="delivery-order-search" class="visually-hidden">{{$t('requisition.search_by_fields')}}</label>
                            <div class="delivery-toolbar__search-control d-flex align-items-center">
                                <BootstrapIcon class="delivery-toolbar__search-icon flex-shrink-0" name="search" :size="18" />
                                <input
                                id="delivery-order-search"
                                v-model="filters.search"
                                type="search"
                                class="delivery-toolbar__search-input flex-fill border-0 bg-transparent"
                                :placeholder="$t('requisition.search_by_fields')"
                                :aria-label="$t('requisition.search_by_fields')"
                                autocomplete="off"
                                >
                                <button
                                v-if="filters.search.length > 0"
                                type="button"
                                class="delivery-toolbar__clear flex-shrink-0 border-0"
                                :aria-label="$t('requisition.clear_search')"
                                :title="$t('requisition.clear_search')"
                                @click="filters.search = ''"
                                >
                                    <BootstrapIcon name="x-lg" :size="13" />
                                </button>
                            </div>
                        </form>
                        <button
                        type="button"
                        class="delivery-toolbar__sync btn btn-primary d-inline-flex align-items-center justify-content-center gap-2 flex-shrink-0"
                        :disabled="loading || pullRefreshing"
                        :aria-label="$t('delivery.sync_orders')"
                        :title="$t('delivery.sync_orders')"
                        @click="get_orders(true)"
                        >
                            <BootstrapIcon color="fill-white text-white" name="arrow-clockwise" :size="18" />
                            <span class="d-none d-sm-inline fw-bolder">{{$t('delivery.sync_orders')}}</span>
                        </button>
                    </div>
                </div>
            </div>
            <DeliveryNotifications />
            <div class="row">
                <div class="col">
                    <div v-if="loading" class="delivery-list-loading text-center" role="status">
                        <span class="spinner-border text-teal-600" aria-hidden="true"></span>
                        <p class="text-gray-600 mt-3 mb-0">{{$t('delivery.loading_orders')}}</p>
                    </div>
                    <div v-else-if="displayLocations.length > 0">
                        <section v-for="location in displayLocations" :key="location.key" class="delivery-location-group mb-3">
                            <header class="delivery-location-group__header">
                                <span class="delivery-location-group__icon flex-shrink-0">
                                    <BootstrapIcon name="geo-alt-fill" :size="18" />
                                </span>
                                <div class="delivery-location-group__title">
                                    <p class="delivery-location-group__eyebrow mb-1">{{$t('delivery.location_group')}}</p>
                                    <h2 class="delivery-location-group__name fw-bolder mb-0">{{location.name}}</h2>
                                </div>
                                <span class="delivery-location-group__count">{{location.orderCountText}}</span>
                            </header>
                            <div class="delivery-location-group__orders">
                                <OrderWidgetCompact v-for="order in location.orders" :key="order.key" :order="order.value" />
                            </div>
                        </section>
                    </div>
                    <div v-else>
                        <EmptyResponse :description="$t('delivery.no_orders')" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EmptyResponse from '@/common/directive/EmptyResponse.vue'
import BootstrapIcon from '@/common/directive/BootstrapIcon'
import OrderWidgetCompact from '@/common/directive/OrderWidgetCompact.vue'
import DeliveryNotifications from '@/components/DeliveryNotifications.vue'
import InventoryOrderStore from '@/common/store/inventory_order.js'

export default {
    components: {
        BootstrapIcon,
        DeliveryNotifications,
        EmptyResponse,
        OrderWidgetCompact
    },
    data: function () {
        return {
            loading: true,
            locations: [],
            filters: {
                search: ''
            },
            timeout: 0,
            pullToRefreshEnabled: false,
            pullTracking: false,
            pullRefreshing: false,
            pullThresholdReached: false,
            pullStartX: 0,
            pullStartY: 0
        }
    },
    computed: {
        displayLocations: function () {
            if (!Array.isArray(this.locations)) return []

            return this.locations
                .filter(location => location && typeof location === 'object' && !Array.isArray(location))
                .map((location, locationIndex) => {
                    const orders = Array.isArray(location.orders)
                        ? location.orders
                            .filter(order => order && typeof order === 'object' && !Array.isArray(order))
                            .map((order, orderIndex) => ({
                                key: typeof order.id === 'string' && order.id.trim() ? order.id : `${locationIndex}-${orderIndex}`,
                                value: order
                            }))
                        : []
                    const countKey = orders.length === 1 ? 'delivery.location_order_count_one' : 'delivery.location_order_count_many'
                    return {
                        key: typeof location.id === 'string' && location.id.trim() ? location.id : `location-${locationIndex}`,
                        name: typeof location.name === 'string' && location.name.trim()
                            ? location.name.trim()
                            : this.$t('delivery.unnamed_location'),
                        orders,
                        orderCountText: this.$t(countKey, { COUNT: orders.length })
                    }
                })
                .filter(location => location.orders.length > 0)
        }
        // is_authorized_member: function () {
        //     return this.$store.state.is_authorized_member
        // },
        // username: function () {
        //     return this.$store.state.member_delivery.username
        // },
        // registration: function () {
        //     return this.$store.state.member_delivery.registration
        // },
        // has_service_worker: function () {
        //     return 'serviceWorker' in navigator
        // }
    },
    watch: {
        filters: {
            deep: true,
            handler: function () {
                clearTimeout(this.timeout)

                const self = this
                this.timeout = setTimeout(async function () {
                    await self.get_orders()
                }, 250)
            }
        }
        // is_authorized_member: function () {
        //     if (!this.is_authorized_member) {
        //         this.$router.replace('/')
        //     }
        // }
    },
    created: async function () {
        // if (!this.is_authorized_member) {
        //     this.$router.replace('/')
        // }

        await this.get_orders(false)
        // await this.get_push_subscription()
    },
    mounted: function () {
        const standaloneDisplay = typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches
        this.pullToRefreshEnabled = window.navigator.standalone === true || standaloneDisplay

        if (this.pullToRefreshEnabled) {
            document.addEventListener('touchstart', this.handlePullStart, { passive: true })
            document.addEventListener('touchmove', this.handlePullMove, { passive: true })
            document.addEventListener('touchend', this.handlePullEnd, { passive: true })
            document.addEventListener('touchcancel', this.cancelPull, { passive: true })
        }
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage)
        }
    },
    beforeUnmount: function () {
        document.removeEventListener('touchstart', this.handlePullStart)
        document.removeEventListener('touchmove', this.handlePullMove)
        document.removeEventListener('touchend', this.handlePullEnd)
        document.removeEventListener('touchcancel', this.cancelPull)
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.removeEventListener('message', this.handleServiceWorkerMessage)
        }
    },
    methods: {
        isInteractivePullTarget: function (target) {
            if (!target || typeof target.closest !== 'function') return false
            return Boolean(target.closest('button, input, select, textarea, [contenteditable="true"], [role="button"]'))
        },
        isAtPageTop: function () {
            return window.scrollY <= 0 && document.documentElement.scrollTop <= 0
        },
        handlePullStart: function (event) {
            if (!this.pullToRefreshEnabled || this.pullRefreshing || this.loading || event.touches.length !== 1) return
            if (!this.isAtPageTop() || this.isInteractivePullTarget(event.target)) return

            this.pullStartX = event.touches[0].clientX
            this.pullStartY = event.touches[0].clientY
            this.pullTracking = true
            this.pullThresholdReached = false
        },
        handlePullMove: function (event) {
            if (!this.pullTracking || event.touches.length !== 1) return

            const distanceX = Math.abs(event.touches[0].clientX - this.pullStartX)
            const distanceY = event.touches[0].clientY - this.pullStartY

            if (!this.isAtPageTop() || distanceY <= 0 || distanceX > distanceY) {
                this.cancelPull()
                return
            }

            if (distanceY < 8) return

            this.pullThresholdReached = distanceY >= 72
        },
        handlePullEnd: async function () {
            if (!this.pullTracking) return

            const shouldRefresh = this.pullThresholdReached
            this.pullTracking = false
            this.pullThresholdReached = false

            if (!shouldRefresh) return

            this.pullRefreshing = true
            try {
                await this.get_orders(false, true)
            } finally {
                this.pullRefreshing = false
            }
        },
        cancelPull: function () {
            this.pullTracking = false
            this.pullThresholdReached = false
        },
        submit: async function ($event) {
            $event.preventDefault()
        },
        handleServiceWorkerMessage: async function (event) {
            if (!event.data || event.data.type !== 'ORDER_PUSH' || this.loading || this.pullRefreshing) return
            await this.get_orders(false, true)
        },
        get_orders: async function (display_notification, background_refresh = false) {
            const query = {}
            if (this.filters.search.length >= 2) {
                query.search = this.filters.search
            }
            if (!background_refresh) this.loading = true
            try {
                const response = await InventoryOrderStore.dispatch('GET_INFO', query)
                if (response && response.data && response.data.status === 0) {
                    this.locations = response.data.locations
                    if (display_notification) {
                        this.$bus.$emit('Notification/show', { title: this.$t('notifications.orders_synchronized'), msg: this.$t('notifications.orders_synchronized_description'), level: 'success' })
                    }
                } else {
                    this.$bus.$emit('Notification/show', { title: this.$t('notifications.something_went_wrong'), msg: this.$t('notifications.please_retry_the_operation_or_refresh'), level: 'danger' })
                }
            } catch (error) {
                this.$bus.$emit('Notification/show', { title: this.$t('notifications.something_went_wrong'), msg: this.$t('notifications.please_retry_the_operation_or_refresh'), level: 'danger' })
            } finally {
                if (!background_refresh) this.loading = false
            }
        }
    }
}
</script>
